import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useScreenShare } from '../context/ScreenShareContext';
import { useKeywords } from '../context/KeywordContext';
import { coordinateMapper } from '../utils/CoordinateMapper';
import EnforcementOverlay from './EnforcementOverlay';
import { OverlayContainer, overlayRegistry } from './overlays';
import WindowActionPanel from './WindowActionPanel';
import GazeOverlay, { GazeHeatmap, useGazeTracking } from './GazeOverlay';
import { useGraffiti, GraffitiCanvas, GraffitiToolbar } from './GraffitiOverlay';
import ScreenshotModal from './ScreenshotModal';
import ScreenShareHistory from './ScreenShareHistory';
import { useFeature } from '../hooks/useFeatures';
import './ScreenSharePanel.css';

// Register built-in overlays
overlayRegistry.register('enforcement', EnforcementOverlay, {
  priority: 150,
  name: 'Time Limit Enforcement'
});

/**
 * ScreenSharePanel - Top dropdown screen share display component
 *
 * Features:
 * - Collapsible dropdown panel from top of main content
 * - Click-to-select window
 * - Window outline overlays
 * - Camera preview (optional)
 * - Quick action buttons
 */
const ScreenSharePanel = ({
  showCamera = false,
  className = ''
}) => {
  const { isConnected, attachAllVideos, featureManager, sendFeatureMessage, connectedPeers, getVideoTrackDiagnostics } = useWebRTC();
  const {
    isPanelVisible,
    isPanelCollapsed,
    toggleCollapsed,
    registerScreenShareVideo,
    nativeResolution,
    videoAspectRatio,
    setVideoAspectRatio,
    selectedWindow
  } = useScreenShare();

  const [hasVideo, setHasVideo] = useState(false);
  const [videoElement, setVideoElement] = useState(null);
  const videoContainerRef = useRef(null);
  const screenShareRef = useRef(null);

  // Control panel mode states
  const [viewMode, setViewMode] = useState('live'); // 'live' or 'history'
  const [controlMode, setControlMode] = useState('monitor'); // 'monitor' (with panel) or 'control' (VNC-like)
  const [graffitiActive, setGraffitiActive] = useState(false);
  const graffitiFeature = useFeature('digital-graffiti');
  const graffiti = useGraffiti(videoElement, graffitiFeature, graffitiActive, nativeResolution);

  // Remote control state - tracks whether desktop's physical input is blocked
  const [remoteInputEnabled, setRemoteInputEnabled] = useState(true);
  const lastMouseMoveRef = useRef(0); // For throttling mouse moves
  const lastCoordLogRef = useRef(0); // For throttling coordinate debug logs

  // Focused window tracking
  const [focusedWindow, setFocusedWindow] = useState(null);
  const [focusedWindowStartTime, setFocusedWindowStartTime] = useState(null);
  const { checkKeywords } = useKeywords();

  // Check if focused window is flagged
  const focusedWindowFlag = useMemo(() => {
    if (!focusedWindow?.title) return null;
    const result = checkKeywords(focusedWindow.title);
    return result.matched ? result : null;
  }, [focusedWindow?.title, checkKeywords]);

  // Gaze tracking state - hoisted to share between header toggle and overlay
  // Uses state (not ref) so hook updates when video element is found
  const gazeState = useGazeTracking(videoElement);

  // Attach videos when component mounts
  useEffect(() => {
    if (!isConnected) return;

    const timer = setTimeout(() => {
      attachAllVideos?.();
    }, 100);

    return () => clearTimeout(timer);
  }, [isConnected, attachAllVideos]);

  // Register video element with ScreenShareContext
  useEffect(() => {
    if (screenShareRef.current) {
      registerScreenShareVideo(screenShareRef.current);
    }
  }, [registerScreenShareVideo]);

  // Setup click handler via window-control feature (only in monitor mode)
  useEffect(() => {
    if (!screenShareRef.current || !featureManager) return;

    const windowControl = featureManager.getFeature('window-control');
    if (!windowControl) return;

    if (controlMode === 'monitor') {
      // Enable window targeting in monitor mode
      windowControl.setupScreenShareClickHandler(
        screenShareRef.current,
        'screen-share-panel'
      );
    } else {
      // Disable window targeting in control mode
      windowControl.removeScreenShareClickHandler(screenShareRef.current);
    }

    return () => {
      // Cleanup on unmount
      if (screenShareRef.current) {
        windowControl.removeScreenShareClickHandler(screenShareRef.current);
      }
    };
  }, [featureManager, hasVideo, controlMode]);

  // Subscribe to foreground window updates for focused indicator
  useEffect(() => {
    if (!featureManager) return;

    const windowControl = featureManager.getFeature('window-control');
    if (!windowControl) return;

    const handleForegroundUpdate = (windowInfo) => {
      if (windowInfo) {
        // Check if this is a different window than before
        if (!focusedWindow || focusedWindow.hwnd !== windowInfo.hwnd) {
          setFocusedWindow(windowInfo);
          setFocusedWindowStartTime(Date.now());
        } else {
          // Same window, just update the info (title might change)
          setFocusedWindow(windowInfo);
        }
      } else {
        setFocusedWindow(null);
        setFocusedWindowStartTime(null);
      }
    };

    windowControl.on('foregroundWindowUpdate', handleForegroundUpdate);

    return () => {
      windowControl.off('foregroundWindowUpdate', handleForegroundUpdate);
    };
  }, [featureManager, focusedWindow]);

  // Setup screenshot drag handler (only in monitor mode)
  useEffect(() => {
    if (!screenShareRef.current || !featureManager) return;

    const screenshotFeature = featureManager.getFeature('screenshot');
    if (!screenshotFeature) return;

    if (controlMode === 'monitor') {
      // Enable drag-to-screenshot in monitor mode
      screenshotFeature.setupDragHandler(screenShareRef.current);

      // Update native resolution when available
      if (nativeResolution.width && nativeResolution.height) {
        screenshotFeature.updateNativeResolution(nativeResolution.width, nativeResolution.height);
      }
    } else {
      // Disable drag-to-screenshot in control mode
      screenshotFeature.removeDragHandlers();
    }

    return () => {
      // Cleanup on unmount
      screenshotFeature.removeDragHandlers();
    };
  }, [featureManager, hasVideo, nativeResolution, controlMode]);

  // Check for video presence and update aspect ratio
  useEffect(() => {
    const checkForVideo = () => {
      const container = videoContainerRef.current;
      if (container) {
        const videos = container.querySelectorAll('video');
        setHasVideo(videos.length > 0);

        // Find the screen share video element
        const screenShareWrapper = container.querySelector('[data-type="Screen-Share"]');
        if (screenShareWrapper) {
          const video = screenShareWrapper.querySelector('video');
          if (video && video !== screenShareRef.current) {
            screenShareRef.current = video;
            setVideoElement(video); // Update state so gaze hook re-renders
            registerScreenShareVideo(video);
          }

          // Update aspect ratio when video has dimensions
          if (video && video.videoWidth && video.videoHeight) {
            const newAspectRatio = video.videoWidth / video.videoHeight;
            setVideoAspectRatio(newAspectRatio);
          }
        }
      }
    };

    checkForVideo();
    const interval = setInterval(checkForVideo, 500);
    return () => clearInterval(interval);
  }, [isConnected, registerScreenShareVideo]);

  // Listen for video metadata to update aspect ratio immediately
  useEffect(() => {
    const video = screenShareRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (video.videoWidth && video.videoHeight) {
        const newAspectRatio = video.videoWidth / video.videoHeight;
        setVideoAspectRatio(newAspectRatio);
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    // Also check immediately in case metadata is already loaded
    handleLoadedMetadata();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [hasVideo]);

  // ============================================
  // Remote Control Event Handlers
  // ============================================

  /**
   * Send remote control message via feature_message protocol
   */
  const sendRemoteControlMessage = useCallback((action, payload) => {
    if (!sendFeatureMessage) {
      console.warn('[RemoteControl] sendFeatureMessage not available');
      return;
    }
    sendFeatureMessage({
      feature: 'remote-control',
      cmd: action,
      payload
    });
  }, [sendFeatureMessage]);

  /**
   * Handle mouse move - throttled to ~60fps
   */
  const handleRemoteMouseMove = useCallback((e) => {
    if (controlMode !== 'control') return;

    // Throttle to ~60fps (16ms)
    const now = performance.now();
    if (now - lastMouseMoveRef.current < 16) return;
    lastMouseMoveRef.current = now;

    // Debug: ensure coordinateMapper has correct references
    if (!coordinateMapper.videoElement) {
      // Set it from our ref if not set
      if (screenShareRef.current) {
        coordinateMapper.setVideoElement(screenShareRef.current);
      }
    }
    if (!coordinateMapper.nativeWidth && nativeResolution.width) {
      coordinateMapper.setNativeResolution(nativeResolution.width, nativeResolution.height);
    }

    const coords = coordinateMapper.clientToScreen(e.clientX, e.clientY);

    // Debug: Log coordinate details periodically
    const logNow = Date.now();
    if (logNow - lastCoordLogRef.current > 2000) {
      lastCoordLogRef.current = logNow;
      const metrics = coordinateMapper.getVideoMetrics();
      console.log('[RemoteControl] Coordinate mapping:', {
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: coords?.screenX,
        screenY: coords?.screenY,
        nativeWidth: metrics?.nativeWidth,
        nativeHeight: metrics?.nativeHeight,
        videoWidth: metrics?.videoWidth,
        videoHeight: metrics?.videoHeight,
        renderedArea: metrics?.renderedArea
      });
    }

    if (coords) {
      sendRemoteControlMessage('mouse_move', {
        x: coords.screenX,
        y: coords.screenY
      });
    }
  }, [controlMode, sendRemoteControlMessage, nativeResolution]);

  /**
   * Handle mouse down
   */
  const handleRemoteMouseDown = useCallback((e) => {
    if (controlMode !== 'control') return;

    const coords = coordinateMapper.clientToScreen(e.clientX, e.clientY);
    if (coords) {
      sendRemoteControlMessage('mouse_down', {
        x: coords.screenX,
        y: coords.screenY,
        button: e.button
      });
    }
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Handle mouse up
   */
  const handleRemoteMouseUp = useCallback((e) => {
    if (controlMode !== 'control') return;

    const coords = coordinateMapper.clientToScreen(e.clientX, e.clientY);
    if (coords) {
      sendRemoteControlMessage('mouse_up', {
        x: coords.screenX,
        y: coords.screenY,
        button: e.button
      });
    }
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Handle mouse wheel
   */
  const handleRemoteMouseWheel = useCallback((e) => {
    if (controlMode !== 'control') return;

    sendRemoteControlMessage('mouse_wheel', {
      deltaX: e.deltaX,
      deltaY: e.deltaY
    });
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Touch → remote mouse. A tap becomes mouse_down + mouse_up (a click); a drag
   * streams mouse_move between them. Lets the console drive the desktop from a
   * touchscreen — previously it was mouse+keyboard only. preventDefault stops the
   * page from scrolling/zooming while controlling.
   */
  const handleRemoteTouchStart = useCallback((e) => {
    if (controlMode !== 'control') return;
    const t = e.touches[0];
    if (!t) return;
    const coords = coordinateMapper.clientToScreen(t.clientX, t.clientY);
    if (coords) {
      sendRemoteControlMessage('mouse_move', { x: coords.screenX, y: coords.screenY });
      sendRemoteControlMessage('mouse_down', { x: coords.screenX, y: coords.screenY, button: 0 });
    }
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  const handleRemoteTouchMove = useCallback((e) => {
    if (controlMode !== 'control') return;
    const t = e.touches[0];
    if (!t) return;
    const coords = coordinateMapper.clientToScreen(t.clientX, t.clientY);
    if (coords) {
      sendRemoteControlMessage('mouse_move', { x: coords.screenX, y: coords.screenY });
    }
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  const handleRemoteTouchEnd = useCallback((e) => {
    if (controlMode !== 'control') return;
    const t = e.changedTouches[0];
    if (!t) return;
    const coords = coordinateMapper.clientToScreen(t.clientX, t.clientY);
    if (coords) {
      sendRemoteControlMessage('mouse_up', { x: coords.screenX, y: coords.screenY, button: 0 });
    }
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Handle key down
   */
  const handleRemoteKeyDown = useCallback((e) => {
    if (controlMode !== 'control') return;

    // Don't capture if focus is in an input field
    const activeEl = document.activeElement;
    if (activeEl && (
      activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      activeEl.isContentEditable
    )) {
      return;
    }

    // Allow certain browser shortcuts to pass through
    if (e.ctrlKey && (e.key === 't' || e.key === 'w' || e.key === 'n' || e.key === 'Tab')) {
      return;
    }

    sendRemoteControlMessage('key_down', {
      code: e.code,
      key: e.key,
      shift: e.shiftKey,
      ctrl: e.ctrlKey,
      alt: e.altKey
    });
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Handle key up
   */
  const handleRemoteKeyUp = useCallback((e) => {
    if (controlMode !== 'control') return;

    // Don't capture if focus is in an input field
    const activeEl = document.activeElement;
    if (activeEl && (
      activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      activeEl.isContentEditable
    )) {
      return;
    }

    sendRemoteControlMessage('key_up', {
      code: e.code,
      key: e.key
    });
    e.preventDefault();
  }, [controlMode, sendRemoteControlMessage]);

  /**
   * Toggle remote input enabled state
   */
  const toggleRemoteInput = useCallback(() => {
    const newState = !remoteInputEnabled;
    setRemoteInputEnabled(newState);
    sendRemoteControlMessage('set_input_enabled', { enabled: newState });
  }, [remoteInputEnabled, sendRemoteControlMessage]);

  // Attach/detach remote control event listeners when control mode changes
  useEffect(() => {
    const video = screenShareRef.current;
    if (!video || controlMode !== 'control') return;

    // Ensure coordinateMapper is properly configured when entering control mode
    coordinateMapper.setVideoElement(video);
    if (nativeResolution.width && nativeResolution.height) {
      coordinateMapper.setNativeResolution(nativeResolution.width, nativeResolution.height);
    }

    // Debug log
    console.log('[RemoteControl] Entering control mode', {
      videoElement: !!video,
      nativeResolution,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      mapperNativeWidth: coordinateMapper.nativeWidth,
      mapperNativeHeight: coordinateMapper.nativeHeight
    });

    // Attach mouse events to video element
    video.addEventListener('mousemove', handleRemoteMouseMove);
    video.addEventListener('mousedown', handleRemoteMouseDown);
    video.addEventListener('mouseup', handleRemoteMouseUp);
    video.addEventListener('wheel', handleRemoteMouseWheel, { passive: false });
    // Touch parity so the console is drivable from a touchscreen.
    video.addEventListener('touchstart', handleRemoteTouchStart, { passive: false });
    video.addEventListener('touchmove', handleRemoteTouchMove, { passive: false });
    video.addEventListener('touchend', handleRemoteTouchEnd, { passive: false });
    const preventContextMenu = (e) => e.preventDefault();
    video.addEventListener('contextmenu', preventContextMenu);

    // Attach keyboard events to document (when video has focus)
    document.addEventListener('keydown', handleRemoteKeyDown);
    document.addEventListener('keyup', handleRemoteKeyUp);

    return () => {
      video.removeEventListener('mousemove', handleRemoteMouseMove);
      video.removeEventListener('mousedown', handleRemoteMouseDown);
      video.removeEventListener('mouseup', handleRemoteMouseUp);
      video.removeEventListener('wheel', handleRemoteMouseWheel);
      video.removeEventListener('touchstart', handleRemoteTouchStart);
      video.removeEventListener('touchmove', handleRemoteTouchMove);
      video.removeEventListener('touchend', handleRemoteTouchEnd);
      video.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', handleRemoteKeyDown);
      document.removeEventListener('keyup', handleRemoteKeyUp);
    };
  }, [controlMode, nativeResolution, handleRemoteMouseMove, handleRemoteMouseDown, handleRemoteMouseUp, handleRemoteMouseWheel, handleRemoteTouchStart, handleRemoteTouchMove, handleRemoteTouchEnd, handleRemoteKeyDown, handleRemoteKeyUp]);

  // Handle header click to toggle
  const handleHeaderClick = useCallback((e) => {
    // Don't toggle if clicking the collapse button or mode toggles
    if (e.target.closest('.collapse-toggle') || e.target.closest('.mode-toggles')) return;
    toggleCollapsed();
  }, [toggleCollapsed]);

  if (!isPanelVisible) {
    return null;
  }

  return (
    <div className={`screen-share-panel ${isPanelCollapsed ? 'collapsed' : 'expanded'} ${className}`}>
      {/* Header bar - always visible, click to toggle */}
      <div className="panel-header" onClick={handleHeaderClick}>
        <div className="panel-header-left">
          <h4>Screen Share</h4>

          {/* Paused indicator - visible when collapsed */}
          {isPanelCollapsed && isConnected && (
            <div className="paused-indicator">
              <span className="paused-icon" />
              <span className="paused-text">PAUSED</span>
            </div>
          )}

          {/* Control Panel Mode Toggles */}
          <div className="mode-toggles">
            {/* View Mode: LIVE / HISTORY */}
            <div className="mode-toggle-group" role="group" aria-label="View mode">
              <button
                className={`mode-toggle ${viewMode === 'live' ? 'active' : ''}`}
                aria-pressed={viewMode === 'live'}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode('live');
                }}
                data-tooltip="Watch the desktop screen in real-time"
              >
                <span className="mode-indicator" aria-hidden="true" />
                LIVE
              </button>
              <button
                className={`mode-toggle ${viewMode === 'history' ? 'active' : ''}`}
                aria-pressed={viewMode === 'history'}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode('history');
                }}
                data-tooltip="Browse recorded screenshots from past sessions"
              >
                <span className="mode-indicator" aria-hidden="true" />
                HISTORY
              </button>
            </div>

            {/* Control Mode: CONTROL / MONITOR */}
            <div className="mode-toggle-group" role="group" aria-label="Control mode">
              <button
                className={`mode-toggle control-mode ${controlMode === 'control' ? 'active' : ''}`}
                aria-pressed={controlMode === 'control'}
                onClick={(e) => {
                  e.stopPropagation();
                  setControlMode('control');
                }}
                data-tooltip="Take over mouse and keyboard — remote desktop control"
              >
                <span className="mode-indicator" aria-hidden="true" />
                CONTROL
              </button>
              <button
                className={`mode-toggle control-mode ${controlMode === 'monitor' ? 'active' : ''}`}
                aria-pressed={controlMode === 'monitor'}
                onClick={(e) => {
                  e.stopPropagation();
                  setControlMode('monitor');
                }}
                data-tooltip="Watch only — click to select and manage windows"
              >
                <span className="mode-indicator" aria-hidden="true" />
                MONITOR
              </button>
            </div>

            {/* Gaze: TRACKER / HEATMAP / OFF */}
            {gazeState.isAvailable && (
              <div className="mode-toggle-group" role="group" aria-label="Gaze overlay">
                <button
                  className={`mode-toggle gaze-mode ${gazeState.mode === 'tracker' ? 'active' : ''}`}
                  aria-pressed={gazeState.mode === 'tracker'}
                  onClick={(e) => { e.stopPropagation(); gazeState.setMode('tracker'); }}
                  data-tooltip="Show where the subject is looking right now (requires Tobii eye tracker)"
                >
                  <span className="mode-indicator" aria-hidden="true" />
                  GAZE TRACKER
                </button>
                <button
                  className={`mode-toggle gaze-mode ${gazeState.mode === 'heatmap' ? 'active' : ''}`}
                  aria-pressed={gazeState.mode === 'heatmap'}
                  onClick={(e) => { e.stopPropagation(); gazeState.setMode('heatmap'); }}
                  data-tooltip="Fading heatmap of where the subject has looked over time"
                >
                  <span className="mode-indicator" aria-hidden="true" />
                  HEATMAP
                </button>
                <button
                  className={`mode-toggle gaze-mode ${gazeState.mode === 'off' ? 'active' : ''}`}
                  aria-pressed={gazeState.mode === 'off'}
                  onClick={(e) => { e.stopPropagation(); gazeState.setMode('off'); }}
                  data-tooltip="Hide the gaze overlay"
                >
                  <span className="mode-indicator" aria-hidden="true" />
                  OFF
                </button>
              </div>
            )}

            {/* Graffiti: ON / OFF */}
            <div className="mode-toggle-group" role="group" aria-label="Graffiti">
              <button
                className={`mode-toggle graffiti-mode ${graffitiActive ? 'active' : ''}`}
                aria-pressed={graffitiActive}
                onClick={(e) => {
                  e.stopPropagation();
                  setGraffitiActive(true);
                }}
                data-tooltip="Draw on the desktop screen — visible to the subject"
              >
                <span className="mode-indicator" aria-hidden="true" />
                GRAFFITI
              </button>
              <button
                className={`mode-toggle graffiti-mode ${!graffitiActive ? 'active' : ''}`}
                aria-pressed={!graffitiActive}
                onClick={(e) => {
                  e.stopPropagation();
                  setGraffitiActive(false);
                }}
                data-tooltip="Stop drawing on screen"
              >
                <span className="mode-indicator" aria-hidden="true" />
                OFF
              </button>
            </div>
          </div>
        </div>

        <button
          className="collapse-toggle"
          onClick={toggleCollapsed}
          data-tooltip={isPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          <i className="simple-icon-arrow-up" />
          {isPanelCollapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {/* Collapsible content */}
      <div className="panel-content">
        {/* Main video section */}
        <div className="video-section">
          {viewMode === 'history' && (
            <ScreenShareHistory
              videoAspectRatio={videoAspectRatio}
              nativeResolution={nativeResolution}
            />
          )}
          <div
            id="screenShareContainer"
            ref={videoContainerRef}
            className={`video-container ${hasVideo ? 'has-video' : 'no-video'} ${focusedWindowFlag ? 'flagged' : ''} ${controlMode === 'control' ? 'control-mode' : ''} ${graffitiActive ? 'graffiti-mode' : ''}`}
            style={{ aspectRatio: videoAspectRatio, display: viewMode === 'live' ? '' : 'none' }}
          >
            <>
              {!isConnected && (
                  <div className="video-placeholder">
                    <i className="iconsminds-wifi" />
                    <p>Not connected</p>
                    <small>Go to Settings to connect</small>
                  </div>
                )}

                {isConnected && !hasVideo && (
                  <div className="video-placeholder">
                    <div className="spinner" />
                    <p>Waiting for video stream...</p>
                    {/* Track diagnostics are for debugging only — never surface the
                        raw "Peers: 1 · Videos in DOM: 0 · [peer] screen:none…" string
                        to the operator in production. */}
                    {import.meta.env.DEV && (
                      <small className="video-diagnostics">
                        {(() => {
                          const peerCount = connectedPeers?.length || 0;
                          const diag = getVideoTrackDiagnostics?.() || [];
                          const container = videoContainerRef.current;
                          const videoCount = container ? container.querySelectorAll('video').length : 0;
                          const parts = [`Peers: ${peerCount}`, `Videos in DOM: ${videoCount}`];
                          if (diag.length === 0) {
                            parts.push('No tracks stored');
                          } else {
                            diag.forEach(d => {
                              parts.push(`[${d.peer}] screen: ${d.screenShare}, active: ${d.screenShareActive}, cam: ${d.camera}`);
                            });
                          }
                          return parts.join(' · ');
                        })()}
                      </small>
                    )}
                  </div>
                )}

                {/* Focused window indicator */}
                {hasVideo && focusedWindow && (
                  <div className={`focused-window-indicator ${focusedWindowFlag ? 'flagged' : ''}`}>
                    <div className="focused-indicator-content">
                      <div className={`focused-status ${focusedWindowFlag ? 'flagged' : 'normal'}`}>
                        {focusedWindowFlag ? (
                          <>
                            <span className="flag-icon">🚩</span>
                            <span className="status-text">Flagged</span>
                          </>
                        ) : (
                          <>
                            <span className="focus-dot" />
                            <span className="status-text">Focused</span>
                          </>
                        )}
                      </div>

                      {/* Flag badges */}
                      {focusedWindowFlag && (
                        <div className="flag-badges">
                          {focusedWindowFlag.groups.map(g => (
                            <span
                              key={g.id}
                              className="flag-badge"
                              style={{ backgroundColor: g.color }}
                              data-tooltip={`Matched: "${g.keyword}"`}
                            >
                              {g.name}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="focused-window-info">
                        {focusedWindow.processName && (
                          <span className="app-name">{focusedWindow.processName}</span>
                        )}
                        <span className="window-title" data-tooltip={focusedWindow.title}>
                          {focusedWindow.title?.substring(0, 60) || 'Untitled'}
                          {focusedWindow.title?.length > 60 ? '...' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Window outlines are rendered by window-control feature via direct DOM manipulation
                    to support drag handles and real-time position updates */}

                {/* Plugin overlays (enforcement, games, etc.) */}
                {hasVideo && <OverlayContainer />}

                {/* Gaze overlays - live reticle (tracker) and fading heatmap */}
                {hasVideo && <GazeOverlay gazeState={gazeState} />}
                {hasVideo && <GazeHeatmap gazeState={gazeState} />}

                {/* Graffiti overlay - drawing on screen */}
                {hasVideo && (
                  <GraffitiCanvas graffiti={graffiti} isActive={graffitiActive} />
                )}

                {/* Remote input toggle - appears in CONTROL mode */}
                {hasVideo && controlMode === 'control' && (
                  <div className="remote-control-overlay">
                    <button
                      className={`remote-input-toggle ${!remoteInputEnabled ? 'input-disabled' : ''}`}
                      onClick={toggleRemoteInput}
                      data-tooltip={remoteInputEnabled ? 'Click to disable remote input' : 'Click to enable remote input'}
                    >
                      <span className={`toggle-indicator ${remoteInputEnabled ? 'enabled' : 'disabled'}`} />
                      {remoteInputEnabled ? 'INPUT ENABLED' : 'INPUT DISABLED'}
                    </button>
                  </div>
                )}

                {/* Floating Window Action Panel - appears when a window is targeted in MONITOR mode */}
                {selectedWindow && hasVideo && controlMode === 'monitor' && (
                  <WindowActionPanel
                    selectedWindow={selectedWindow}
                    videoContainerRef={videoContainerRef}
                    nativeResolution={nativeResolution}
                  />
                )}
            </>
          </div>

          {/* Resolution info */}
          {nativeResolution.width && (
            <div className="resolution-info">
              <small>{nativeResolution.width} x {nativeResolution.height}</small>
            </div>
          )}

          {/* Graffiti toolbar - appears below video when graffiti mode active */}
          {graffitiActive && <GraffitiToolbar graffiti={graffiti} />}
        </div>

        {/* Camera preview - hidden by default */}
        {showCamera && (
          <div className="camera-section">
            <h5>Camera</h5>
            <div id="cameraPreviewContainer" className="camera-container" />
          </div>
        )}
      </div>

      {/* Screenshot modal - displays captured screenshots */}
      <ScreenshotModal />
    </div>
  );
};

export default ScreenSharePanel;
